import { useEffect, useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout, TableCard } from "ui-components/Layout";
import { EditButton, CancelButton } from "ui-components/IconButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import { useCustomerValue } from "providers/CustomerContext";
import { getPOList, getPOStatusCount, deletePO } from "services/data.inbound.service";
import POLog from "./components/POLog";
import { statusObj } from "layouts/views/common/enum";
import { useTeamValue } from "providers/TeamContext";
function POList() {
  const {permissions, allowAll} = useTeamValue();
  const { customerCode } = useCustomerValue();
  const enabled = Boolean(customerCode)
  const navigate = useNavigate();
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Open");
  const [tabs, setTabs] = useState([{label:"Open",count:0},{label:"Cancelled",count:0},{label:"Received",count:0}]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);

  const addPermission = allowAll || (Boolean(permissions["Purchase Order"]) && permissions["Purchase Order"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["Purchase Order"]) && permissions["Purchase Order"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["Purchase Order"]) && permissions["Purchase Order"].findIndex(b => b==="deactivate") !== -1);

  useEffect(() => {
    if (location.state) {
      setAlert(<Success content={location.state} onChange={setAlert} />);
    }
  }, []);

  useQuery({
    queryKey: ["po-list", { customerCode, status }],
    queryFn: () => getPOList({ customerCode, status: statusObj[status] }),
    enabled,
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["po-list", "status", { customerCode,status }],
    queryFn: () => getPOStatusCount({ customerCode }),
    enabled,
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deletePO, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["po-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });

  const onEdit = (req) => navigate("/inbound/po/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Cancel / Force Close PO Number ${req.poNumber}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const [logRequest, setLogRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const logProp = { open, setOpen, logRequest, setLogRequest };

  const columns = [
    {
      header: "PO Number",
      accessorKey: "poNumber",
      cell: ({ row }) => {
        const { poNumber, customerCode,isPartialRecv } = row.original;
        const color = isPartialRecv ? "#4CAF50" : "#1A73E8"
        return (
          <span
            style={{ color, cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              setOpen(true);
              setLogRequest({ poNumber, customerCode });
            }}
          >
            {poNumber}
          </span>
        );
      },
    },
    { header: "Vendor Code", accessorKey: "vendorCode" },
    { header: "PO Date", accessorKey: "poDate" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { poNumber, customerCode } = row.original;
        return status === "Open" ? (
          <>
            <EditButton title="edit po" disabled={!editPermission} onClick={() => onEdit({ poNumber, customerCode })} />
            <CancelButton title="cancel po" disabled={!deactivatePermission} onClick={() => onDelete({ poNumber, customerCode })} />
          </>
        ) : (
          <></>
        );
      },
    },
  ];

  return (
    <ListLayout>
      <POLog {...logProp} />
       
      {dialog}
      <MDBox my={3}>
      {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" alignItems="flex-start" margin="10px">
          <MDButton
            sx={{ borderRadius: "5px" }}
            variant="gradient"
            color="info"
            disabled={!addPermission}
            onClick={() => navigate("/inbound/po/add")}
          >
            <Icon>add</Icon>&nbsp;po
          </MDButton>
          <MDButton
            sx={{ marginLeft: "1rem", borderRadius: "5px" }}
            variant="outlined"
            color="dark"
            disabled={!addPermission}
            onClick={() => navigate("/inbound/po/upload")}
          >
            <Icon>upload</Icon>&nbsp; po
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default POList;
